import { Component, Input, OnDestroy, OnInit, forwardRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { ContractService } from '../../../service/entity-services/contract.service';
import { TimecardsService } from '../../../service/entity-services/timecards.service';
import { Contract } from '../../../objects/contract';
import { ContractInfo } from '../../../objects/timecard-spec';

/**
 * Multi-select picker showing contracts associated with a party or its parent.
 *
 * Used by:
 *   - The timecard activation flow (step 2 dialog after "Enable Timecards" confirm)
 *   - NewEditTimecardComponent's Associated Contracts card
 *
 * Filter logic lives in `ContractService.contractsForParty()`. Denormalization
 * to ContractInfo lives in `TimecardsService.buildContractInfo()`. This widget
 * is a thin wire-up — keep it that way; do not duplicate filter or denorm
 * logic here.
 *
 * Works with `formControlName` (ControlValueAccessor) — value is `ContractInfo[]`.
 * Compares by contractId so selections survive a re-render with fresh option
 * references.
 */
@Component({
  selector: 'app-contracts-for-party-multi-select',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContractsForPartyMultiSelectComponent),
      multi: true
    }
  ],
  template: `
    <div class="picker-shell">
      @if (loading()) {
        <div class="picker-state">
          <mat-spinner diameter="24"></mat-spinner>
          <span>Loading contracts…</span>
        </div>
      } @else if (availableContracts().length === 0) {
        <div class="picker-state empty">
          <mat-icon>info</mat-icon>
          <span>{{ emptyMessage }}</span>
        </div>
      } @else {
        <p class="picker-hint">{{ hint }}</p>
        <div class="picker-list">
          @for (c of availableContracts(); track c.contractId) {
            <mat-checkbox
              [checked]="isSelected(c.contractId!)"
              [disabled]="disabled()"
              (change)="onToggle(c, $event.checked)">
              <span class="contract-row">
                <span class="contract-name">{{ c.nameOfContract || c.contractId }}</span>
                @if (c.directVendorFriendlyName) {
                  <span class="contract-meta">{{ c.directVendorFriendlyName }}</span>
                }
                @if (c.expirationDate) {
                  <span class="contract-meta">expires {{ c.expirationDate | date:'mediumDate' }}</span>
                }
              </span>
            </mat-checkbox>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }
    .picker-shell { display: flex; flex-direction: column; gap: 8px; }
    .picker-state {
      display: flex; align-items: center; gap: 8px;
      padding: 12px; background: #f5f5f5; border-radius: 4px; color: #555;
    }
    .picker-state.empty { background: #fff8e1; color: #5d4037; }
    .picker-hint { margin: 0 0 4px; color: #666; font-size: 13px; }
    .picker-list {
      display: flex; flex-direction: column; gap: 6px;
      max-height: 320px; overflow-y: auto;
      border: 1px solid #e0e0e0; border-radius: 4px; padding: 8px;
    }
    .contract-row {
      display: inline-flex; flex-direction: column; gap: 2px; line-height: 1.3;
    }
    .contract-name { font-weight: 500; }
    .contract-meta { font-size: 12px; color: #666; }
  `]
})
export class ContractsForPartyMultiSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private contractService = inject(ContractService);
  private timecardsService = inject(TimecardsService);
  private destroy$ = new Subject<void>();

  /** The HCP's own vendorNum (child party). */
  @Input() partyNum: string | null | undefined;
  /** The HCP's parent agency vendorNum, if any. */
  @Input() parentVendorNum: string | null | undefined;
  /** Hint text shown above the list. */
  @Input() hint = 'Select the contracts this HCP works under. At least one is required.';
  /** Message shown when the filter returns zero contracts. */
  @Input() emptyMessage = 'No contracts found for this party or its parent. Add a contract first, then return here.';

  loading = signal(true);
  availableContracts = signal<Contract[]>([]);
  disabled = signal(false);

  /** Persisted form value — array of ContractInfo snapshots. */
  value: ContractInfo[] = [];

  private onChange: (val: ContractInfo[]) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    // Subscribe to visibleContracts$ — the active-status cache that
    // contractsForParty() reads from. We don't use the emitted value
    // (the helper does its own active + not-expired filtering); we just
    // need a tick to re-run the filter when the cache updates.
    this.contractService.visibleContracts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshAvailableContracts());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── ControlValueAccessor ───────────────────────────────

  writeValue(val: ContractInfo[] | null | undefined): void {
    this.value = Array.isArray(val) ? [...val] : [];
  }

  registerOnChange(fn: (val: ContractInfo[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // ── Internal ───────────────────────────────────────────

  private refreshAvailableContracts(): void {
    const list = this.contractService.contractsForParty([this.partyNum, this.parentVendorNum]);
    this.availableContracts.set(list);
    this.loading.set(false);
  }

  isSelected(contractId: string): boolean {
    return this.value.some(ci => ci.contractId === contractId);
  }

  onToggle(contract: Contract, checked: boolean): void {
    const id = contract.contractId;
    if (!id) return;

    if (checked) {
      if (this.value.some(ci => ci.contractId === id)) return;
      const info = this.timecardsService.buildContractInfo(contract);
      if (!info) return;
      this.value = [...this.value, info];
    } else {
      this.value = this.value.filter(ci => ci.contractId !== id);
    }

    this.onChange(this.value);
    this.onTouched();
  }
}
